import { useEffect, useState } from 'react';

import { ApiError } from '../../../api/client';
import { getDocumentTypeBySlug } from '../../schema/api';

const DEBOUNCE_MS = 300;
// Bound the suggestion search so a pathological tenant with hundreds of variants doesn't hang
// the UI. 20 is plenty for the realistic "invoice" / "invoice-2" / … case.
const MAX_SUGGESTION_PROBES = 20;

export type SlugAvailabilityState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'free' }
  | { kind: 'taken'; suggestion: string | null };

/**
 * Probes the backend for slug availability with debounce + auto-suggests the first free
 * `slug-N` variant when the requested one is taken. Returns 'free' on 404, 'taken' on 200.
 * Other errors short-circuit to 'idle' so the publish gate stays the authoritative check.
 */
export function useSlugAvailability(rawSlug: string): SlugAvailabilityState {
  const slug = rawSlug.trim();
  const [state, setState] = useState<SlugAvailabilityState>({ kind: 'idle' });

  useEffect(() => {
    if (slug === '') {
      setState({ kind: 'idle' });
      return;
    }

    let cancelled = false;
    setState({ kind: 'checking' });

    const handle = setTimeout(() => {
      void (async () => {
        const taken = await isSlugTaken(slug);
        if (cancelled) return;
        if (taken === null) {
          setState({ kind: 'idle' });
          return;
        }
        if (!taken) {
          setState({ kind: 'free' });
          return;
        }
        const suggestion = await findFreeSuggestion(slug);
        if (cancelled) return;
        setState({ kind: 'taken', suggestion });
      })();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [slug]);

  return state;
}

async function isSlugTaken(slug: string): Promise<boolean | null> {
  try {
    await getDocumentTypeBySlug(slug);
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return false;
    }
    // Network/server errors fall back to "no opinion" — the backend gate still protects us.
    return null;
  }
}

async function findFreeSuggestion(base: string): Promise<string | null> {
  for (let i = 2; i < 2 + MAX_SUGGESTION_PROBES; i++) {
    const candidate = `${base}-${i}`;
    const taken = await isSlugTaken(candidate);
    if (taken === false) {
      return candidate;
    }
    if (taken === null) {
      return null;
    }
  }
  return null;
}
